import { computedAsync, until } from "@vueuse/core";

import {
    EmailAuthProvider,
    AuthError as FirebaseAuthError,
    GoogleAuthProvider,
    MultiFactorError,
    MultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier,
    TotpMultiFactorGenerator,
    TotpSecret,
    User,
    createUserWithEmailAndPassword as firebaseCreateUserWithEmailAndPassword,
    isSignInWithEmailLink as firebaseIsSignInWithEmailLink,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    sendSignInLinkToEmail as firebaseSendSignInLinkToEmail,
    signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
    signInWithEmailLink as firebaseSignInWithEmailLink,
    getMultiFactorResolver,
    multiFactor,
    reauthenticateWithCredential,
    sendEmailVerification,
    signInWithCredential,
    signInWithPopup,
    updateProfile,
} from "firebase/auth";
import { defineStore, storeToRefs } from "pinia";
import { ToastMessageOptions } from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";
import { useRouter } from "vue-router";

import { useReauthenticateDialog } from "@/composables/reauthenticate-dialog";
import { useTotpDialog } from "@/composables/totp-dialog";
import { useVerifyEmailDialog } from "@/composables/verify-email-dialog";
import config from "@/config";
import { firebaseAuth, provider } from "@/firebase";
import { isHttpExceptionModel, useServices } from "@/lib/openapi";

export enum AuthStatus {
    // There is a brief period prior to the initialization of the
    // authentication where the AuthStatus is unknown.
    UNKNOWN,
    SIGNED_IN,
    NOT_SIGNED_IN,
    MFA_REQUIRED,
    MFA_ENROLLMENT_REQUIRED,
    EMAIL_VERIFICATION_REQUIRED,
}

export class AuthError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AuthError";
        Object.setPrototypeOf(this, AuthError.prototype);
    }
}

/**
 * Check whether error is a firebase AuthError.
 */
function isFirebaseAuthError(error: unknown): error is FirebaseAuthError {
    if (
        error instanceof Error &&
        "code" in error &&
        typeof error.code === "string"
    ) {
        return error?.code.startsWith("auth/");
    } else {
        return false;
    }
}

export const useAuthenticationStore = defineStore("authentication", () => {
    const router = useRouter();
    const toast = useToast();
    const api = useServices({
        opts: { throwOnError: true },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });
    const { emulatorUrl } = config.authentication.firebase;

    const authUser = ref({
        email: "",
        photoUrl: "",
        mfaEnabled: false,
    });
    const authStatus = ref(AuthStatus.UNKNOWN);
    const isUnregisteredUser = ref(false);
    const isInitialized = until(authStatus).not.toBe(AuthStatus.UNKNOWN);
    const mfaResolver = ref<MultiFactorResolver>();
    const totpSecret = ref<TotpSecret>();
    const phoneVerificationId = ref("");
    const { open: openReauthenticateDialog } = useReauthenticateDialog();
    const { open: openTotpDialog } = useTotpDialog();
    const { open: openVerifyEmailDialog } = useVerifyEmailDialog();

    const enrollPhoneMfa = async () => {
        const recaptchaVerifier = new RecaptchaVerifier(
            firebaseAuth,
            "recaptcha-container",
            {
                size: "invisible",
            }
        );

        const multiFactorSession = await multiFactor(
            firebaseAuth.currentUser!
        ).getSession();

        const phoneInfoOptions = {
            // Only used in the emulator
            phoneNumber: "+31612345678",
            session: multiFactorSession,
        };

        const phoneAuthProvider = new PhoneAuthProvider(firebaseAuth);
        try {
            phoneVerificationId.value =
                await phoneAuthProvider.verifyPhoneNumber(
                    phoneInfoOptions,
                    recaptchaVerifier
                );
            const verificationCode = prompt("Enter SMS verification code:");
            if (verificationCode) {
                const cred = PhoneAuthProvider.credential(
                    phoneVerificationId.value,
                    verificationCode
                );
                const multiFactorAssertion =
                    PhoneMultiFactorGenerator.assertion(cred);
                await multiFactor(firebaseAuth.currentUser!).enroll(
                    multiFactorAssertion,
                    "Phone Number"
                );
                authStatus.value = AuthStatus.SIGNED_IN;
                authUser.value.mfaEnabled = true;
            }
        } finally {
            recaptchaVerifier.clear();
        }
    };

    /**
     * Get the TOTP Secret and URI for a new enrollment.
     */
    const getTotpSecret = async () => {
        const multiFactorSession = await multiFactor(
            firebaseAuth.currentUser!
        ).getSession();
        try {
            totpSecret.value =
                await TotpMultiFactorGenerator.generateSecret(
                    multiFactorSession
                );
            const totpUri = totpSecret.value.generateQrCodeUrl(
                firebaseAuth.currentUser!.email!,
                "MyStekz"
            );
            return {
                totpSecret: totpSecret.value,
                totpUri,
            };
        } catch (error) {
            const success = await _handleAuthError(error);
            if (success) {
                return getTotpSecret();
            }
        }
    };

    /**
     * Verify the enrollment of a new TOTP.
     */
    const verifyTotpEnrollment = async (verificationCode: string) => {
        const multiFactorAssertion =
            TotpMultiFactorGenerator.assertionForEnrollment(
                totpSecret.value!,
                verificationCode
            );
        try {
            await multiFactor(firebaseAuth.currentUser!).enroll(
                multiFactorAssertion,
                "Authenticator App"
            );
            authStatus.value = AuthStatus.SIGNED_IN;
        } catch (error) {
            authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
            const success = await _handleAuthError(error);
            if (success) {
                return verifyTotpEnrollment(verificationCode);
            }
            throw error;
        }
    };

    /**
     * Verify OTP.
     */
    const verifyOtp = async (otp: string) => {
        const multiFactorAssertion =
            TotpMultiFactorGenerator.assertionForSignIn(
                mfaResolver.value!.hints[0].uid,
                otp
            );
        await mfaResolver.value!.resolveSignIn(multiFactorAssertion);
    };

    const verifyPhone = async (otp: string) => {
        const cred = PhoneAuthProvider.credential(
            phoneVerificationId.value,
            otp
        );
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
        await mfaResolver.value!.resolveSignIn(multiFactorAssertion);
    };

    /**
     * Unenroll the user from OTP so that they can reset it.
     */
    const resetOtp = async () => {
        const user = multiFactor(firebaseAuth.currentUser!);
        try {
            if (user.enrolledFactors.length) {
                await user.unenroll(user.enrolledFactors[0]);
            }
            authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
        } catch (error) {
            if (isFirebaseAuthError(error)) {
                const success = await _handleAuthError(error);
                if (success) {
                    await resetOtp();
                } else {
                    throw Error("Authentication failed.");
                }
            }
            throw error;
        }
        // Now setup TOTP again.
        return await openTotpDialog({ enrollment: true });
    };

    const _handleMfaRequired = async (error: MultiFactorError) => {
        mfaResolver.value = getMultiFactorResolver(firebaseAuth, error);

        if (emulatorUrl) {
            // For emulator, check if phone MFA is already enrolled
            const phoneFactorHints = mfaResolver.value.hints.filter(
                (hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID
            );

            if (phoneFactorHints.length > 0) {
                // Phone MFA already enrolled, verify with SMS
                const recaptchaVerifier = new RecaptchaVerifier(
                    firebaseAuth,
                    "recaptcha-container",
                    {
                        size: "invisible",
                    }
                );

                try {
                    const phoneAuthProvider = new PhoneAuthProvider(
                        firebaseAuth
                    );
                    phoneVerificationId.value =
                        await phoneAuthProvider.verifyPhoneNumber(
                            {
                                multiFactorHint: phoneFactorHints[0],
                                session: mfaResolver.value.session,
                            },
                            recaptchaVerifier
                        );

                    const verificationCode = prompt(
                        "Enter SMS verification code:"
                    );
                    if (verificationCode) {
                        await verifyPhone(verificationCode);
                        return true;
                    }
                    return false;
                } finally {
                    recaptchaVerifier.clear();
                }
            } else {
                // No phone MFA enrolled yet, show enrollment is required
                authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
                return false;
            }
        } else {
            const enrolledFactors = mfaResolver.value.hints.map(
                (info) => info.displayName
            );
            return await handleTotpWithDialog(!enrolledFactors.length);
        }
    };

    /*
     * Show the popup for TOTP and handle auth statusses accordingly.
     */
    const handleTotpWithDialog = async (enrollment: boolean) => {
        let success: boolean;
        if (enrollment) {
            authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
            success = await openTotpDialog({ enrollment: true });
        } else {
            authStatus.value = AuthStatus.MFA_REQUIRED;
            success = await openTotpDialog({ enrollment: false });
        }
        if (success) {
            authStatus.value = AuthStatus.SIGNED_IN;
            authUser.value.mfaEnabled = true;
        }
        return success;
    };

    /**
     * Show the popup for a user to verify their email and handle auth statusses accordingly.
     */
    const verifyEmailWithDialog = async () => {
        const prevAuthStatus = authStatus.value;
        authStatus.value = AuthStatus.EMAIL_VERIFICATION_REQUIRED;

        const success = await openVerifyEmailDialog();
        if (success) {
            authStatus.value = prevAuthStatus;
        }
        return success;
    };

    /**
     * Generaic function to handle sign in errors across multiple sign in methods.
     */
    const _handleAuthError = async (error: unknown) => {
        if (!isFirebaseAuthError(error)) {
            throw error;
        }

        switch (error.code) {
            case "auth/multi-factor-auth-required":
                return await _handleMfaRequired(error as MultiFactorError);
            case "auth/requires-recent-login":
                return await openReauthenticateDialog();
            case "auth/user-token-expired":
                await signOut({
                    severity: "error",
                    summary: "Session expired",
                    detail: "Your session has expired. Please sign in again.",
                    life: 5000,
                });
                return false;
            case "auth/unverified-email":
                return await verifyEmailWithDialog();
            case "auth/network-request-failed":
                toast.add({
                    severity: "error",
                    summary: "Cannot connect to authentication server.",
                });
                throw error;
            case "auth/weak-password":
                throw new AuthError(
                    "Weak password. Please use a longer password."
                );
            case "auth/email-already-in-use":
                throw new AuthError(
                    "An account already exists for this email address."
                );
            case "auth/password-does-not-meet-requirements":
                throw new AuthError("Password requirements not met.");
            default:
                throw error;
        }
    };

    const reAuthenticateEmailAndPassword = async (password: string) => {
        const credential = EmailAuthProvider.credential(
            authUser.value.email,
            password
        );
        try {
            await reauthenticateWithCredential(
                firebaseAuth.currentUser!,
                credential
            );
        } catch (error) {
            console.log("Failed to reauhtenticate", error);
            await _handleAuthError(error);
        }
    };

    const signInWithEmailAndPassword = async (
        email: string,
        password: string
    ) => {
        try {
            const credential = await firebaseSignInWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            await credential.user.reload();
            const idToken = await credential.user.getIdTokenResult();
            const hasMfaEnrolled =
                multiFactor(credential.user).enrolledFactors.length > 0;

            if (!idToken?.signInSecondFactor && !hasMfaEnrolled) {
                authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
                if (emulatorUrl) {
                    // Use phone MFA for emulator
                    await enrollPhoneMfa();
                } else {
                    // Use TOTP for production
                    const success = await openTotpDialog({ enrollment: true });
                    if (success) {
                        authStatus.value = AuthStatus.SIGNED_IN;
                    }
                }
            } else {
                authStatus.value = AuthStatus.SIGNED_IN;
            }
        } catch (error) {
            await _handleAuthError(error);
        }
    };

    const isSignInWithEmailLink = () => {
        return firebaseIsSignInWithEmailLink(
            firebaseAuth,
            window.location.href
        );
    };

    // Not used due to too low daily limit: https://firebase.google.com/docs/auth/limits#email_sending_limits
    const signInWithEmailLink = async () => {
        const email = window.localStorage.getItem("emailForSignIn");
        if (!email) {
            throw Error(
                "Failed to sign you in. A link can only be used once. Don't forget to use the same browser and session to login as you requested the link with."
            );
        }
        try {
            await firebaseSignInWithEmailLink(firebaseAuth, email);
            window.localStorage.removeItem("emailForSignIn");
        } catch (error) {
            await _handleAuthError(error);
        }
    };

    const sendSignInLinkToEmail = async (email: string) => {
        await firebaseSendSignInLinkToEmail(firebaseAuth, email, {
            url: `${import.meta.env.VITE_BASE_URL}/login`,
            handleCodeInApp: true,
        });
        window.localStorage.setItem("emailForSignIn", email);
        return;
    };

    const signInWithGoogle = async () => {
        try {
            return await signInWithPopup(firebaseAuth, provider);
        } catch (error) {
            await _handleAuthError(error);
        }
    };

    const signOut = async (message?: ToastMessageOptions) => {
        if (message) {
            toast.add(message);
        }
        await firebaseAuth.signOut();
        router.push({ name: "home" });
    };

    // Retrieve the token that the API calls to the backend need.
    const getToken = async (): Promise<string | undefined> => {
        // Wait until we're in a stable auth state (not in transition)
        await until(authStatus).toMatch(
            (status) =>
                status === AuthStatus.SIGNED_IN ||
                status === AuthStatus.NOT_SIGNED_IN
        );

        if (authStatus.value === AuthStatus.SIGNED_IN) {
            const user = firebaseAuth.currentUser;
            if (user) {
                const token = await user.getIdToken();
                return token;
            }
        }

        return undefined;
    };

    firebaseAuth.onAuthStateChanged(async (firebaseUser) => {
        try {
            if (firebaseUser) {
                await firebaseUser.reload();
            }
            const idToken = await firebaseUser?.getIdTokenResult();
            const hasMfaEnrolled = firebaseUser
                ? multiFactor(firebaseUser).enrolledFactors.length > 0
                : false;

            if (
                idToken?.signInProvider === "password" &&
                !idToken?.signInSecondFactor &&
                !hasMfaEnrolled
            ) {
                authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
            } else if (firebaseUser) {
                authStatus.value = AuthStatus.SIGNED_IN;
            } else {
                authStatus.value = AuthStatus.NOT_SIGNED_IN;
            }

            authUser.value = {
                email: firebaseUser?.email || "",
                photoUrl: getGoogleProviderData(firebaseUser)?.photoURL || "",
                mfaEnabled: !!idToken?.signInSecondFactor || hasMfaEnrolled,
            };
        } catch (error) {
            await _handleAuthError(error);
        }
    });

    const getGoogleProviderData = (firebaseUser: User | null) => {
        if (firebaseUser && firebaseUser.providerData) {
            const googleProvider = firebaseUser.providerData.find(
                (provider) => provider.providerId === "google.com"
            );
            return googleProvider || undefined;
        }
        return undefined;
    };

    const signupUserWithEmailAndPassword = async (
        email: string,
        password: string,
        name: string
    ) => {
        isUnregisteredUser.value = true;

        try {
            const credential = await firebaseCreateUserWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            if (!credential || !credential.user) {
                throw Error("Sign up with email failed.");
            }
            const idToken = await credential.user.getIdTokenResult();
            const hasMfaEnrolled =
                multiFactor(credential.user).enrolledFactors.length > 0;

            // Verify email address
            if (!credential.user.emailVerified) {
                await verifyEmailWithDialog();
            }

            // Setup MFA second factor
            if (!idToken?.signInSecondFactor && !hasMfaEnrolled) {
                authStatus.value = AuthStatus.MFA_ENROLLMENT_REQUIRED;
                if (emulatorUrl) {
                    // Use phone MFA for emulator
                    await enrollPhoneMfa();
                } else {
                    // Use TOTP for production
                    await handleTotpWithDialog(true);
                }
            }
            return await signupUser(email, name);
        } catch (error) {
            await _handleAuthError(error);
        } finally {
            isUnregisteredUser.value = false;
        }
    };

    const signupUserWithGoogle = async () => {
        isUnregisteredUser.value = true;
        try {
            const credential = await signInWithGoogle();
            if (!credential || !credential.user) {
                throw Error("Sign up with Google failed.");
            }
            return await signupUser(
                credential.user.email!,
                credential.user.displayName!
            );
        } catch (error) {
            await _handleAuthError(error);
        } finally {
            isUnregisteredUser.value = false;
        }
    };

    /**
     * Create the user in our backend (idempotent operation).
     */
    const signupUser = async (email: string, name: string) => {
        // Create backend user (idempotent operation)
        try {
            const result = await api.signupUserSignupPost({
                body: {
                    email: email,
                    name: name,
                },
            });
            // Success - navigate to home
            console.log("User signup successful, navigating to home");
            router.push("/");
            return result;
        } catch (error) {
            if (isHttpExceptionModel(error)) {
                const isUserExistsError =
                    error.detail[0].type === "ALREADY_EXISTS";
                if (isUserExistsError) {
                    // User already exists - this could be either:
                    // 1. User is fully set up (success, continue)
                    // 2. User setup is incomplete (error shown)
                    // The backend handles this distinction, so if we get here
                    // with ALREADY_EXISTS, the user is complete
                    console.log(
                        "User already exists and is complete, continuing"
                    );
                    router.push("/");
                    return;
                } else {
                    // For other errors, show error message and stay on page
                    toast.add({
                        severity: "error",
                        summary: "Sign up failed",
                        detail:
                            error.detail[0].message ||
                            "Sign up failed. Please try again.",
                        life: 5000,
                    });
                    // Re-throw to prevent continuing signup flow
                    throw error;
                }
            }
            // Re-throw unexpected errors
            throw error;
        }
    };

    /**
     * Signs in a user with given credentials. Only works for emulator, in any other case nothing happens.
     */
    const signInToEmulator = async (email: string, name?: string) => {
        if (emulatorUrl) {
            const displayName =
                name || email.split("@")[0].replace(/[._-]/g, " ");
            const credential = await signInWithCredential(
                firebaseAuth,
                GoogleAuthProvider.credential(
                    JSON.stringify({
                        sub: "fake-sub",
                        email,
                        email_verified: true,
                    })
                )
            );
            if (credential.user) {
                await updateProfile(credential.user, { displayName });
            }
        }
    };

    const sendPasswordResetEmail = async (email: string) => {
        try {
            await firebaseSendPasswordResetEmail(firebaseAuth, email, {
                url: `${import.meta.env.VITE_BASE_URL}/login`,
                handleCodeInApp: true,
            });
            return true;
        } catch (error) {
            await _handleAuthError(error);
            return false;
        }
    };

    /**
     * Check if user needs to accept current policy versions
     */
    const checkPolicyAcceptanceRequired = async (): Promise<{
        required: boolean;
        isNewUser: boolean;
    }> => {
        try {
            const response = await api.getPolicyStatusUserPolicyStatusGet({});

            // The response might be wrapped, try to access data property or use response directly
            const data = response.data;

            const required = Boolean(
                data?.needs_privacy_policy_acceptance ||
                    data?.needs_terms_conditions_acceptance
            );
            const isNewUser =
                !data?.privacy_policy_accepted &&
                !data?.terms_conditions_accepted;

            return { required, isNewUser };
        } catch (error) {
            // If we can't check policy status, assume acceptance is required
            console.warn("Could not check policy status:", error);
            return {
                required: true,
                isNewUser: true,
            };
        }
    };

    /**
     * Record user's policy acceptance
     */
    const recordPolicyAcceptance = async (
        acceptPrivacyPolicy: boolean,
        acceptTermsConditions: boolean,
        policyVersions: { privacy: string; terms: string }
    ) => {
        console.log("Recording policy acceptance:", {
            acceptPrivacyPolicy,
            acceptTermsConditions,
            policyVersions,
        });
        const promises = [];

        if (acceptPrivacyPolicy) {
            promises.push(
                api.recordPolicyAcceptanceUserPolicyAcceptancePost({
                    body: {
                        policy_type: "privacy_policy",
                        version: policyVersions.privacy,
                    },
                })
            );
        }

        if (acceptTermsConditions) {
            promises.push(
                api.recordPolicyAcceptanceUserPolicyAcceptancePost({
                    body: {
                        policy_type: "terms_conditions",
                        version: policyVersions.terms,
                    },
                })
            );
        }

        try {
            const results = await Promise.all(promises);
            console.log("Policy acceptance recorded successfully:", results);
        } catch (error) {
            console.error("Failed to record policy acceptance:", error);
            throw error;
        }
    };

    return {
        // External auth status. Follows the internal auth status, but
        // doesn't update to SIGNED_IN until the user exists in the backend.
        authStatus: computedAsync(
            async () => {
                if (authStatus.value === AuthStatus.SIGNED_IN) {
                    await until(isUnregisteredUser).not.toBeTruthy();
                    return authStatus.value;
                } else {
                    return authStatus.value;
                }
            },
            authStatus.value,
            { lazy: true }
        ),
        isInitialized,
        authUser,
        verifyOtp,
        getTotpSecret,
        verifyTotpEnrollment,
        getToken,
        reAuthenticateEmailAndPassword,
        signInWithEmailAndPassword,
        signInWithEmailLink,
        isSignInWithEmailLink,
        sendSignInLinkToEmail,
        sendVerificationEmail: () =>
            sendEmailVerification(firebaseAuth.currentUser!),
        checkEmailVerification: async () => {
            // First check if we already know it's verified
            if (firebaseAuth.currentUser?.emailVerified) {
                return true;
            }
            // If not, refresh the token to get the latest email verification status
            await firebaseAuth.currentUser?.reload();
            return firebaseAuth.currentUser?.emailVerified || false;
        },
        signInWithGoogle,
        signOut,
        signInToEmulator,
        resetOtp,
        signupUserWithEmailAndPassword,
        signupUserWithGoogle,
        sendPasswordResetEmail,
        checkPolicyAcceptanceRequired,
        recordPolicyAcceptance,
    };
});

export const useAuthenticationManager = () =>
    storeToRefs(useAuthenticationStore());
